import { getTheme, getCoverGradient, type GiftTheme } from "@/lib/gift-utils";
import { Gift, Play, Share2, MessageCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface GiftPreviewMockupProps {
  heroName: string;
  customMessage: string;
  senderName: string;
  eventName: string;
  style: "light" | "black";
}

export function GiftPreviewMockup({
  heroName,
  customMessage,
  senderName,
  eventName,
  style,
}: GiftPreviewMockupProps) {
  const theme = getTheme(style);
  const displayName = heroName.trim() || "Мария";
  const coverGradient = getCoverGradient(displayName, style);
  const accent = style === "black" ? "#D4AF37" : "#6C63FF";

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground text-center">
        Так будет выглядеть страница
      </p>
      <AnimatePresence mode="wait">
        <motion.div
          key={style}
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.97 }}
          transition={{ duration: 0.35, ease: "easeInOut" }}
          className="relative mx-auto rounded-xl overflow-hidden border max-sm:max-w-[240px] max-sm:h-[260px]"
        style={{
          width: "100%",
          maxWidth: 300,
          height: 340,
          background: theme.bg,
          borderColor: style === "black" ? "rgba(212,175,55,0.15)" : "rgba(108,99,255,0.1)",
        }}
      >
        {/* Spotlight glow */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 rounded-full blur-3xl opacity-40"
          style={{
            width: 160,
            height: 80,
            background: theme.spotlightColor,
          }}
        />

        <div className="relative flex flex-col items-center pt-5 px-4 gap-3">
          {/* Gift icon */}
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: theme.badgeBg }}
          >
            <Gift className="w-5 h-5" style={{ color: accent }} />
          </div>

          {/* Event badge */}
          <span
            className="text-[9px] px-2 py-0.5 rounded-full font-medium"
            style={{
              background: theme.badgeBg,
              color: theme.badgeText,
            }}
          >
            {eventName.trim() || "🎂 День рождения"}
          </span>

          {/* Hero name */}
          <h3
            className="text-base font-bold text-center leading-tight"
            style={{ color: theme.textPrimary }}
          >
            {displayName}, это для тебя!
          </h3>

          {/* Mini cover + player */}
          <div
            className="w-full rounded-lg overflow-hidden"
            style={{
              background: theme.cardBg,
              border: theme.cardBorder,
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <div className="flex items-center gap-2.5 p-2.5">
              {/* Album art */}
              <div
                className="w-11 h-11 rounded-md flex-shrink-0 flex items-center justify-center"
                style={{ background: coverGradient }}
              >
                <Play className="w-4 h-4 text-white/90 fill-white/90" />
              </div>
              {/* Track info */}
              <div className="flex-1 min-w-0 space-y-1">
                <div
                  className="h-2 rounded-full w-3/4"
                  style={{ background: theme.glowColor }}
                />
                <div
                  className="h-1.5 rounded-full w-1/2"
                  style={{ background: theme.glowColor, opacity: 0.6 }}
                />
                {/* Progress bar */}
                <div className="flex items-center gap-1.5 pt-0.5">
                  <div
                    className="h-1 rounded-full flex-1"
                    style={{ background: theme.glowColor }}
                  >
                    <div
                      className="h-full rounded-full w-1/3"
                      style={{ background: accent }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Message block */}
          {(customMessage.trim() || senderName.trim()) && (
            <div
              className="w-full rounded-lg p-2.5 flex gap-2 items-start"
              style={{
                background: theme.messageBg,
                border: theme.messageBorder,
              }}
            >
              <MessageCircle
                className="w-3 h-3 mt-0.5 flex-shrink-0"
                style={{ color: accent, opacity: 0.6 }}
              />
              <div className="min-w-0">
                {customMessage.trim() && (
                  <p
                    className="text-[9px] leading-snug line-clamp-2"
                    style={{ color: theme.textSecondary }}
                  >
                    «{customMessage.trim()}»
                  </p>
                )}
                {senderName.trim() && (
                  <p
                    className="text-[8px] mt-0.5"
                    style={{ color: theme.textTertiary }}
                  >
                    — {senderName.trim()}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Share buttons */}
          <div className="flex gap-2">
            {["TG", "VK", "WA"].map((label) => (
              <div
                key={label}
                className="px-3 py-1 rounded-full text-[8px] font-medium"
                style={{
                  border: `1px solid ${theme.shareBorder}`,
                  color: theme.shareText,
                }}
              >
                {label}
              </div>
            ))}
          </div>
        </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
